/**
 * DraftPersistentService
 *
 * Stores multiple draft entries (document + preview key) in localStorage as an array.
 * Preview Blob is stored separately in IndexedDB via DraftPreviewStore.
 *
 * Entry schema:
 *   { id: string, document: EditorDocument, previewKey: string, updatedAt: number }
 */

import {STORAGE_KEYS} from '../constants/storageKeys'

const LIST_KEY = STORAGE_KEYS.DRAFT_SESSION_LIST
const OLD_KEY = STORAGE_KEYS.DRAFT_SESSION

function generateId() {
    return 'draft_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)
}

function readList() {
    const raw = localStorage.getItem(LIST_KEY)
    return raw ? JSON.parse(raw) : []
}

function writeList(list) {
    localStorage.setItem(LIST_KEY, JSON.stringify(list))
}

function migrateFromSingleEntry() {
    const oldRaw = localStorage.getItem(OLD_KEY)
    if (!oldRaw) return
    try {
        const oldEntry = JSON.parse(oldRaw)
        if (oldEntry && oldEntry.document) {
            const migratedEntry = {
                id: generateId(),
                document: oldEntry.document,
                previewKey: oldEntry.previewKey || '',
                updatedAt: oldEntry.updatedAt || Date.now(),
            }
            const currentList = readList()
            currentList.push(migratedEntry)
            writeList(currentList)
        }
    } catch {
        // Ignore parse errors on old data
    }
    localStorage.removeItem(OLD_KEY)
}

const DraftPersistentService = {
    /**
     * Get all draft entries, sorted by updatedAt descending (most recent first).
     * Performs migration from old single-entry format on first call if needed.
     * @returns {Array<{ id: string, document: object, previewKey: string, updatedAt: number }>}
     */
    getAll() {
        migrateFromSingleEntry()
        const list = readList()
        return list.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
    },

    /**
     * Save or update a draft entry by id.
     * If entry with id exists, it is updated; otherwise a new entry is added.
     * @param {{ id: string, document: object, previewKey: string, updatedAt?: number }} entry
     */
    saveOne(entry) {
        const list = readList()
        const idx = list.findIndex((e) => e.id === entry.id)
        const toSave = {
            ...entry,
            updatedAt: entry.updatedAt ?? Date.now(),
        }
        if (idx >= 0) {
            list[idx] = toSave
        } else {
            list.push(toSave)
        }
        writeList(list)
    },

    /**
     * Load a single draft entry by id.
     * @param {string} id
     * @returns {{ id: string, document: object, previewKey: string, updatedAt: number } | null}
     */
    loadOne(id) {
        const list = readList()
        return list.find((e) => e.id === id) ?? null
    },

    /**
     * Delete a draft entry by id.
     * @param {string} id
     */
    deleteOne(id) {
        const list = readList()
        const filtered = list.filter((e) => e.id !== id)
        writeList(filtered)
    },

    /**
     * Remove all draft entries.
     */
    clearAll() {
        localStorage.removeItem(LIST_KEY)
    },

    /**
     * Generate a new unique draft id.
     * @returns {string}
     */
    generateId,

    /**
     * @deprecated Use getAll() instead. Kept for backward compatibility during transition.
     */
    load() {
        const all = this.getAll()
        return all.length > 0 ? all[0] : null
    },

    /**
     * @deprecated Use saveOne() instead. Kept for backward compatibility during transition.
     */
    save(document, previewKey) {
        const entry = {
            id: generateId(),
            document,
            previewKey,
            updatedAt: Date.now(),
        }
        this.saveOne(entry)
    },

    /**
     * @deprecated Use clearAll() instead.
     */
    clear() {
        this.clearAll()
    },
}

export default DraftPersistentService
